import * as go from 'gojs';

import {
  DRAFT_BACKGROUND_COLOR,
  DRAFT_BACKGROUND_OUTLINE_WIDTH,
  DRAFT_OUTLINE_COLOR,
  MODEL_CONCEPT_ATTRIBUTES_BADGE_SIZE,
  MODEL_CONCEPT_ATTRIBUTES_LIST_LIMIT,
  MODEL_CONCEPT_ATTRIBUTES_LIST_WIDTH,
  NODE_FONT_COLOR,
  NODE_FONT_FACE,
  NODE_FONT_SIZE,
  NODE_FONT_WEIGHT,
  NODE_SIZE,
  NODE_TEXT_WIDTH_RATIO,
} from 'src/containers/ProjectDiagram/constants/styles';
import { toggleIsAttributesOpen } from 'src/containers/ProjectDiagram/handlers/modelConceptClass';
import {
  dataBinding,
  modelBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isClassAttributesModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  determineValueFromDraft,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { ModelConceptClass } from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import * as copy from 'src/templates/copy';

const $ = go.GraphObject.make;

const createModelConceptClosedAttributesTemplate = () =>
  $(
    go.Panel,
    {
      isActionable: true,
      click: toggleIsAttributesOpen(true),
    },
    dataBinding<go.Panel, ModelConceptClass>()(
      'visible',
      'isAttributesOpen',
      (isAttributesOpen) => !isAttributesOpen
    ),

    $(
      go.Shape,
      {
        width: MODEL_CONCEPT_ATTRIBUTES_BADGE_SIZE,
        height: MODEL_CONCEPT_ATTRIBUTES_BADGE_SIZE,
        figure: 'Rectangle',
        fill: 'white',
        stroke: 'black',
        strokeWidth: 1,
      },
      modelBinding<go.Shape, ModelData>()(
        'fill',
        '',
        determineColorFromSelection(
          isClassAttributesModified,
          'white',
          DRAFT_BACKGROUND_COLOR
        )
      ),
      modelBinding<go.Shape, ModelData>()(
        'stroke',
        '',
        determineColorFromSelection(
          isClassAttributesModified,
          'black',
          DRAFT_OUTLINE_COLOR
        )
      ),
      modelBinding<go.Shape, ModelData>()(
        'strokeWidth',
        '',
        determineValueFromDraft(
          isClassAttributesModified,
          1,
          DRAFT_BACKGROUND_OUTLINE_WIDTH
        )
      )
    ),

    $(
      go.TextBlock,
      {
        verticalAlignment: go.Spot.Center,
        stroke: NODE_FONT_COLOR,
        font: `${NODE_FONT_WEIGHT} ${NODE_FONT_SIZE}px ${NODE_FONT_FACE}`,
        overflow: go.TextOverflow.Ellipsis,
        wrap: go.Wrap.DesiredSize,
        width: MODEL_CONCEPT_ATTRIBUTES_BADGE_SIZE,
        height: MODEL_CONCEPT_ATTRIBUTES_BADGE_SIZE,
        textAlign: 'center',
        maxLines: 2,
      },
      modelBinding<go.TextBlock, ModelData>()(
        'text',
        'attributesByIri',
        (attributesByIri, obj) => {
          const { value: iri } = obj.part?.data;
          let count = 0;
          // eslint-disable-next-line guard-for-in, no-restricted-syntax
          for (const attributeIri in attributesByIri) {
            const attribute = attributesByIri[attributeIri];
            if (attribute.domains[iri]) {
              count += 1;
            }
          }
          return count.toString();
        }
      ),
      modelBinding<go.TextBlock, ModelData>()(
        'stroke',
        '',
        determineColorFromSelection(isClassAttributesModified, 'black')
      )
    )
  );

const createModelConceptOpenAttributesTemplate = () =>
  $(
    go.Panel,
    go.Panel.Vertical,
    {
      toolTip: null,
      stretch: go.Stretch.Fill,
      visible: false,
      itemTemplate: $(
        go.Panel,
        'Auto',
        {
          isActionable: true,
          click: toggleIsAttributesOpen(false),
          cursor: 'pointer',
          width: MODEL_CONCEPT_ATTRIBUTES_LIST_WIDTH,
        },
        $(
          go.Shape,
          'Rectangle',
          {
            fill: 'white',
            stroke: 'black',
            strokeWidth: 1,
          },
          modelBinding<go.Shape, ModelData>()(
            'fill',
            '',
            determineColorFromSelection(
              isClassAttributesModified,
              'white',
              DRAFT_BACKGROUND_COLOR
            )
          ),
          modelBinding<go.Shape, ModelData>()(
            'stroke',
            '',
            determineColorFromSelection(
              isClassAttributesModified,
              'black',
              DRAFT_OUTLINE_COLOR
            )
          ),
          modelBinding<go.Shape, ModelData>()(
            'strokeWidth',
            '',
            determineValueFromDraft(
              isClassAttributesModified,
              1,
              DRAFT_BACKGROUND_OUTLINE_WIDTH
            )
          )
        ),
        $(
          go.TextBlock,
          new go.Binding('text'),
          {
            margin: 2,
            stroke: NODE_FONT_COLOR,
            font: `${NODE_FONT_WEIGHT} ${NODE_FONT_SIZE}px ${NODE_FONT_FACE}`,
            overflow: go.TextOverflow.Ellipsis,
            wrap: go.Wrap.DesiredSize,
            width: NODE_TEXT_WIDTH_RATIO * NODE_SIZE,
            textAlign: 'center',
            maxLines: 2,
          },

          modelBinding<go.TextBlock, ModelData>()(
            'stroke',
            '',
            determineColorFromSelection(isClassAttributesModified, 'black')
          )
        )
      ),
    },
    dataBinding<go.Panel, ModelConceptClass>()('visible', 'isAttributesOpen'),
    modelBinding<go.Panel, ModelData>()(
      'itemArray',
      '',
      ({ attributesByIri, labelsByIri }, obj) => {
        const { value: iri } = obj.part?.data;
        const items = [];
        // eslint-disable-next-line guard-for-in, no-restricted-syntax
        for (const attributeIri in attributesByIri) {
          if (items.length === MODEL_CONCEPT_ATTRIBUTES_LIST_LIMIT) {
            return items;
          }
          const attribute = attributesByIri[attributeIri];
          if (attribute.domains[iri]) {
            const text = getLabelForIri(
              labelsByIri,
              attribute.value,
              copy.components.projectDiagram.modelConceptAttribute.new
            );
            items.push({ text, iri: attribute.value });
          }
        }
        return items;
      }
    ),
    $(go.Shape, {
      width: MODEL_CONCEPT_ATTRIBUTES_LIST_WIDTH,
      figure: 'Rectangle',
      fill: 'white',
      stroke: 'black',
    })
  );

export const createAttributesPanel = () =>
  $(
    go.Panel,
    go.Panel.Spot,
    {
      name: 'ATTRIBUTES',
      alignment: new go.Spot(0.875, 0.875),
    },

    modelBinding<go.Panel, ModelData>()('visible', '', (modelData, obj) => {
      const { value: iri } = obj.part?.data;
      const hiddenModels = modelData.hiddenModelIds;
      const models = modelData.modelsByIri[iri];
      const isHidden = models?.every((model) => hiddenModels?.includes(model));
      if (isHidden) {
        return false;
      }
      // eslint-disable-next-line guard-for-in, no-restricted-syntax
      for (const attributeIri in modelData.attributesByIri) {
        const attribute = modelData.attributesByIri[attributeIri];
        if (attribute.domains[iri]) {
          return true;
        }
      }
      return false;
    }),

    createModelConceptClosedAttributesTemplate(),
    createModelConceptOpenAttributesTemplate()
  );
